/*
 * Framework-free, accessible modal shell for the embedded popup form.
 *
 * `openModal` wraps whatever DOM the html/react render produces in a centered
 * card + backdrop, moves focus into the dialog and traps it, and closes on ESC,
 * a backdrop click, or the X button (restoring focus to the previously focused
 * element). It sets `role="dialog"` + `aria-modal="true"` and skips the open
 * animation under `prefers-reduced-motion`. Plain DOM only — no preact/react
 * imports, since this ships inside the standalone embed bundle.
 *
 * Only one modal is active at a time; opening a second tears the first down.
 *
 * `options.layout` (docs/popup-formats-and-targeting_plan.md Phase 2) swaps the
 * card's own dimensions and, for two shapes, the modal semantics themselves:
 * absent or `"modal"` is the centered card described above; `"fullscreen"`
 * (task 2.2) fills the viewport and keeps every semantic — dialog role, focus
 * trap, Esc, backdrop click — identical; `"slide-in"` (task 2.3) is a corner
 * card and `"bar"` (task 2.4) a full-width strip flush to a viewport edge,
 * both non-modal: no backdrop, no focus trap (`aria-modal="false"`), Esc still
 * closes and restores focus, Tab can leave the card for the rest of the page.
 * A bar also lays its content out as a wrapping row rather than the vertical
 * stack every other layout leaves to the caller's own markup.
 *
 * When an owner has chosen nothing (`options.layout` is absent) and the
 * visitor's primary pointer is touch, `openModal` substitutes an internal
 * `"sheet"` default (docs/popup-triggers-and-funnel_plan.md task 3.2) instead
 * of the centered card: full width, flush to the bottom edge, capped at 30% of
 * the viewport height, but otherwise a plain modal (backdrop, focus trap,
 * `aria-modal="true"`) — the touch-only answer to the same question the
 * centered default answers on a computer. It only ever fills in for
 * `undefined`; any explicit choice, `"modal"` included, is never overridden.
 * `"sheet"` is not a value `FormPopupSettings.layout` can store — there is
 * nothing for an owner to pick, since this is what "I haven't picked one"
 * means on a touch device.
 */

import type { FormPopupSettings } from "../lib/types";
import { currentDeviceIsTouch } from "./popupTriggers";

/**
 * `styleOverlay`/`styleCard`'s layout parameter, widened past
 * `FormPopupSettings["layout"]` to include the touch-only default (see the
 * module comment). Never stored, never passed in by a caller — `openModal`
 * computes it once per open.
 */
type ResolvedLayout = FormPopupSettings["layout"] | "sheet";

/** A touch-default sheet never covers more than this share of the viewport. */
const TOUCH_SHEET_MAX_HEIGHT_VH = 30;

export interface OpenModalOptions {
  /** Called when the user dismisses via ESC, backdrop, or the X button. */
  onClose?: () => void;
  /**
   * Force-disable the open animation. Defaults to the user's
   * `prefers-reduced-motion` setting when omitted.
   */
  reducedMotion?: boolean;
  /**
   * A string of `--kiroku-*` declarations (as produced by
   * `generateCssVariables`) to set on the dialog card. Because the form host is
   * appended inside the card, the card chrome and the form inherit the same
   * theme variables. `--kiroku-color-background` and `--kiroku-color-text`
   * additionally repaint the card's and close button's actual `background`/
   * `color` (see `applyThemeVars`), so a dark theme gives a dark card rather
   * than a white one with unused custom properties. Omit to keep the
   * hardcoded default card styling.
   */
  vars?: string;
  /**
   * The popup's shape (`FormPopupSettings.layout`). See the module comment for
   * what each value does; absent behaves as `"modal"` on a non-touch visitor,
   * or the touch-only bottom-sheet default (task 3.2) on one.
   */
  layout?: FormPopupSettings["layout"];
  /**
   * Where a `"slide-in"` card anchors, or which edge a `"bar"` sits against
   * (`FormPopupSettings.position`); ignored by `"modal"`/`"fullscreen"`.
   * `"bottom-right"`/`"bottom-left"` are slide-in's corners; `"top"`/`"bottom"`
   * are the bar's edges — each layout ignores the other's values and falls
   * back to its own default, matching the engine defaults the type's own doc
   * comment names (`"bottom-right"` for slide-in, `"bottom"` for bar).
   */
  position?: FormPopupSettings["position"];
  /**
   * The form's `settings.customCSS.code`: arbitrary CSS the owner wrote for
   * this form. Scoped under `[data-kiroku-popup="dialog"]` before it is
   * applied (see `applyCustomCss`), so a bare selector in it styles only this
   * popup card, never every matching element on the host page. Omit or pass
   * an empty/whitespace-only string to skip.
   */
  customCSS?: string;
  /**
   * Accessible name for the dialog (`aria-label`). A `role="dialog"` with no
   * name is announced as just "dialog". Omit it and the content's own first
   * heading is used instead; pass one when the content has no heading, or
   * renders after the modal opens, or should be announced as something other
   * than what its heading says.
   */
  label?: string;
  /**
   * The form's `settings.privacyPolicyUrl`. When set, and when the content has
   * not drawn a reassurance line of its own, the card gets one under the form:
   * "By submitting this form you agree to our Privacy Policy."
   *
   * Pass the setting the form already carries rather than a popup-only value:
   * one policy link, one place to change it, and the modal says what the inline
   * form says. Omit it when the content renders its own notice (LiveForm does).
   * A URL whose scheme is not http(s) renders nothing, since this markup lands
   * on the customer's page.
   */
  privacyPolicyUrl?: string;
  /**
   * A yes/no screen shown before `contentEl`, for triggers the visitor did not
   * ask for (`docs/popup-offer-screen_plan.md`). `contentEl` is not appended
   * to the DOM at all until "Yes" swaps it in, so the screen carries no
   * assumption about the card's width, the backdrop, or the focus trap, and a
   * later layout (slide-in, full-screen, bar) can host it unchanged. Initial
   * focus lands on the Yes button rather than the usual first-focusable rule.
   * The question becomes the dialog's accessible name via the same
   * first-heading rule `label` above documents. A blank or omitted
   * `yesLabel`/`noLabel` falls back to `DEFAULT_OFFER_YES_TEXT`/
   * `DEFAULT_DECLINE_TEXT` here, the one place both the embed and the
   * dashboard preview open this screen through, rather than in each caller.
   */
  offer?: {
    question: string;
    yesLabel?: string;
    noLabel?: string;
    /** Called after `contentEl` is revealed and focused, never before — so a
     * throwing handler still leaves the form visible. */
    onYes: () => void;
    /** Called after the modal has already closed, the same as a user
     * dismissal but distinct from `onClose` (the caller treats it as a
     * decline, not a close). */
    onNo: () => void;
  };
}

interface ActiveModal {
  /**
   * The element appended directly to `document.body`, removed whole on close.
   * The dimming overlay for every layout that has one; the card itself for
   * `"slide-in"`/`"bar"`, neither of which has a backdrop to wrap it (see
   * `openModal`).
   */
  root: HTMLElement;
  card: HTMLElement;
  previouslyFocused: HTMLElement | null;
  onClose?: () => void;
  keydownHandler: (event: KeyboardEvent) => void;
  /**
   * `root`'s sibling body children this modal marked `inert` (empty for
   * `"slide-in"`/`"bar"`, which are not modal). `restoreInert` removes the
   * attribute from exactly these on close, so a sibling that already carried
   * `inert` before this modal opened is never touched.
   */
  inertSiblings: HTMLElement[];
}

const Z_INDEX = 2147483000; // just under the 32-bit max, above host chrome
const ANIM_MS = 160;

/** Elements that can receive keyboard focus for the trap. */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/*
 * Presentation for the decline control, shared by every surface
 * that renders one: the embed (kiroku-embed.ts), our own marketing island
 * (PopupCTA.tsx) and the owner's preview (PopupPreviewButton.tsx). It lives
 * here, beside the modal shell all three open, because three copies of an
 * inline style drift apart quietly: nothing type-checks a button back into a
 * link. tests/guard/popup-decline-peer-weight.test.ts holds the three together.
 *
 * The control is a peer of the submit button, sized off the same theme
 * variables that style it (research §7: someone who wants to decline should
 * not have to hunt for a 14px underlined link, and the category's reputation
 * comes from making them). It stays secondary through fill alone, an outline
 * against the filled primary, so the modal still has one dominant action.
 * Every variable carries a fallback, because the marketing island and the
 * dashboard preview render outside the embed's injected stylesheet.
 */
export const DECLINE_BUTTON_STYLE = {
  display: "inline-block",
  padding:
    "var(--kiroku-spacing-small, 0.5rem) var(--kiroku-spacing-large, 1.5rem)",
  fontSize: "var(--kiroku-font-size, 1rem)",
  fontWeight: 500,
  lineHeight: "1.5",
  color: "#334155",
  background: "transparent",
  border: "1px solid #94a3b8",
  borderRadius: "var(--kiroku-border-radius, 0.375rem)",
  cursor: "pointer",
} as const;

/*
 * Wording for the decline control when the form owner leaves the label blank,
 * shared by the same three surfaces as the styling above and by the settings
 * panel that offers it as a placeholder.
 *
 * Neutral on purpose (research §7): the departing reader is refusing an offer,
 * not admitting to anything, so the label says only that the answer is no for
 * now. What this rules out is a default written in the visitor's own voice, so
 * that dismissing the box means agreeing with a sentence about themselves;
 * that is the practice NN/g condemns and the reason the category is disliked.
 * An owner may still type whatever they like into the label field, which is
 * their copy on their site. tests/guard/popup-decline-default-copy.test.ts
 * holds the four surfaces to this one string and scans our defaults for it.
 */
export const DEFAULT_DECLINE_TEXT = "Not right now";

/**
 * Wording for the offer screen's accept button when the form owner leaves the
 * label blank (`docs/popup-offer-screen_plan.md`). The No side of that screen
 * falls back to `DEFAULT_DECLINE_TEXT` above rather than a second constant, so
 * "No" reads the same on the offer screen as on the explicit decline control.
 */
export const DEFAULT_OFFER_YES_TEXT = "Yes, show me";

/** The row the decline control sits in, under the form's own submit button. */
export const DECLINE_ROW_STYLE = {
  display: "flex",
  justifyContent: "center",
  marginTop: "var(--kiroku-spacing-medium, 1rem)",
} as const;

/*
 * The modal's one reassurance line.
 *
 * Research §1 (B1/B2): trust signals interact sub-additively, and more than two
 * seals can lower completion, so a popup carries one privacy line rather than a
 * stack of badges. The shell is where that cap can hold, because it is the only
 * code that sees the whole card: the form host, whatever the caller rendered
 * into it, and the decline row.
 *
 * The wording matches LiveForm's inline notice (kiroku-privacy-notice), and the
 * URL is the form's existing `settings.privacyPolicyUrl`, so a popup needs no
 * second control that can disagree with the form it opens.
 * tests/guard/form-privacy-notice.test.ts holds the two together.
 */
export const REASSURANCE_PREFIX = "By submitting this form you agree to our ";
export const REASSURANCE_LINK_TEXT = "Privacy Policy";

/**
 * What already counts as the card's reassurance line: one the shell drew, or
 * LiveForm's own, which the marketing island renders inside this same card.
 */
const REASSURANCE_SELECTOR = "[data-kiroku-reassurance],.kiroku-privacy-notice";

/**
 * The URL as it can safely go in an `href`, or null.
 *
 * This markup is injected into the customer's page, so a `javascript:` or
 * `data:` URL stored on the form would run in their origin, and a form is
 * embeddable across sites. Anything with a scheme other than http(s) is
 * dropped; a relative or protocol-relative path carries no scheme and is kept.
 *
 * A URL still holding a control character after trimming is dropped whole,
 * because the browser drops those characters before it reads the scheme: a
 * newline inside "java\nscript:" defeats the scheme test above and then runs
 * anyway. No real policy URL has one in the middle.
 */
function safeHref(raw?: string): string | null {
  if (typeof raw !== "string") return null;
  const url = raw.trim();
  if (!url || hasControlCharacter(url)) return null;
  const scheme = url.match(/^([a-z][a-z0-9+.-]*):/i);
  if (scheme && !/^https?$/i.test(scheme[1])) return null;
  return url;
}

/** Any C0 control or DEL, tested by code point so no literal one is in here. */
function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/** The reassurance line itself: one sentence, with a real link in it. */
function buildReassurance(href: string): HTMLElement {
  const line = document.createElement("p");
  line.setAttribute("data-kiroku-reassurance", "");
  Object.assign(line.style, {
    margin: "var(--kiroku-spacing-medium, 1rem) 0 0",
    fontSize: "0.75rem",
    lineHeight: "1.5",
    color: "#64748b",
    textAlign: "center",
  } as Partial<CSSStyleDeclaration>);

  const link = document.createElement("a");
  link.href = href;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.textContent = REASSURANCE_LINK_TEXT;
  link.style.color = "inherit";
  link.style.textDecoration = "underline";

  line.appendChild(document.createTextNode(REASSURANCE_PREFIX));
  line.appendChild(link);
  line.appendChild(document.createTextNode("."));
  return line;
}

/**
 * Leave the card with at most one reassurance line: the content's own if it has
 * one, else one built from `privacyPolicyUrl`, else none. Extras are removed,
 * so content that stacks two ends up showing the first.
 */
function applyReassurance(host: HTMLElement, privacyPolicyUrl?: string): void {
  // `host` is the scrolling body, not the card: the line belongs with the
  // content it reassures about, and it scrolls with it.
  const existing = Array.from(
    host.querySelectorAll<HTMLElement>(REASSURANCE_SELECTOR),
  );
  for (const extra of existing.slice(1)) extra.remove();
  if (existing.length > 0) return;
  const href = safeHref(privacyPolicyUrl);
  if (href) host.appendChild(buildReassurance(href));
}

let active: ActiveModal | null = null;

/** Respect the OS reduced-motion preference; assume motion when unknown. */
function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
}

/**
 * Focusable descendants of the card, in DOM order. We filter out the `hidden`
 * attribute but not layout-hidden nodes: `offsetParent` is unreliable (always
 * null in jsdom), and modal content we render is visible by construction.
 */
function focusable(card: HTMLElement): HTMLElement[] {
  return Array.from(
    card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => !el.hasAttribute("hidden"));
}

/**
 * Keep Tab focus inside the card, wrapping at both ends.
 *
 * "Outside" is the case that makes this a trap rather than a wrapper. Focus
 * does not stay in the card just because we put it there: the host page can
 * take it back at any time (an `autofocus` field further down, a chat widget
 * booting, a script restoring focus after its own work), and the card itself
 * holds focus after a click on its padding, since it is `tabindex="-1"`.
 * From any of those positions the next Tab is the one that decides whether the
 * page behind the modal is reachable, so both directions pull focus back in.
 */
function trapTab(event: KeyboardEvent, card: HTMLElement): void {
  const items = focusable(card);
  if (items.length === 0) {
    event.preventDefault();
    card.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const current = document.activeElement;
  const outside = current === card || !card.contains(current);

  if (event.shiftKey) {
    if (current === first || outside) {
      event.preventDefault();
      last.focus();
    }
  } else if (current === last || outside) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Mark every direct child of `document.body` other than `root` as `inert`,
 * so the page behind a modal is unreachable by Tab and invisible to a screen
 * reader (task 4.2, docs/popup-exit-signals-fix_plan.md). Returns the
 * elements it actually marked, so `restoreInert` can undo exactly that and
 * nothing else: a sibling that already carried `inert` (the host page's own
 * choice) is left alone, both while the modal is open and after it closes.
 *
 * `inert` is set and read as a plain attribute (`hasAttribute`/
 * `setAttribute`), not the `.inert` IDL property: jsdom 29 does not implement
 * that property's reflection (verified directly: setting `.inert` leaves
 * `getAttribute("inert")` null), while the attribute methods behave the same
 * way here as in every browser that ships `inert` at all.
 *
 * A throw marking one sibling does not stop the rest from being marked, so a
 * host page with a misbehaving element does not leave the rest of itself
 * reachable behind the popup.
 */
function applyInertToSiblings(root: HTMLElement): HTMLElement[] {
  const marked: HTMLElement[] = [];
  for (const child of Array.from(document.body.children)) {
    if (child === root || !(child instanceof HTMLElement)) continue;
    if (child.hasAttribute("inert")) continue;
    try {
      child.setAttribute("inert", "");
      marked.push(child);
    } catch {
      // Leave this one as it was; still try the rest.
    }
  }
  return marked;
}

/**
 * Undo exactly what `applyInertToSiblings` did. A throw removing one
 * element's attribute does not stop the others from being restored.
 */
function restoreInert(marked: HTMLElement[]): void {
  for (const el of marked) {
    try {
      el.removeAttribute("inert");
    } catch {
      // Move on; one stuck sibling should not leave the rest inert.
    }
  }
}

/** Headings the dialog can borrow its accessible name from. */
const HEADING_SELECTOR = "h1,h2,h3,h4,h5,h6";

let headingSeq = 0;

/** The element's id, minting a collision-free one first if it has none. */
function ensureId(el: HTMLElement): string {
  if (el.id) return el.id;
  let id = `kiroku-popup-title-${(headingSeq += 1)}`;
  while (document.getElementById(id)) {
    id = `kiroku-popup-title-${(headingSeq += 1)}`;
  }
  el.id = id;
  return id;
}

/**
 * Give the dialog an accessible name: the caller's `label`, else the content's
 * own first heading, referenced by `aria-labelledby`.
 *
 * A heading inside a `role="dialog"` does not name it. The name comes from
 * `aria-label` or `aria-labelledby` and from nothing else, so a popup opening
 * with "Join the newsletter" across the top of the card was still announced as
 * a bare "dialog", leaving a screen-reader visitor to work out what had
 * interrupted them from the fields alone. Only the dashboard preview passes a
 * label; the embedded form customers ship renders an `<h2>` from
 * `settings.heading` and passed nothing, which is the case this covers.
 *
 * An existing id is reused rather than replaced, since the host page may be
 * pointing at that heading itself. A blank or absent heading leaves the dialog
 * unnamed: `aria-labelledby` at an empty node announces worse than no name.
 */
function nameDialog(
  card: HTMLElement,
  content: HTMLElement,
  label?: string,
): void {
  if (label) {
    card.setAttribute("aria-label", label);
    return;
  }
  const heading = content.matches(HEADING_SELECTOR)
    ? content
    : content.querySelector<HTMLElement>(HEADING_SELECTOR);
  if (heading?.textContent?.trim()) {
    card.setAttribute("aria-labelledby", ensureId(heading));
  }
}

/** Move initial focus into the dialog (first focusable, else the card). */
function focusInto(card: HTMLElement): void {
  const items = focusable(card);
  if (items.length > 0) {
    items[0].focus();
  } else {
    card.focus();
  }
}

/**
 * Focus the first focusable descendant of `el` specifically, falling back
 * when it has none. Used to reveal the offer screen's form host: `focusInto`
 * would land on the card's close X, which sits before the scroller — and
 * therefore before `el` — in DOM order (see `buildCloseButton`'s call site).
 */
function focusFirstWithin(el: HTMLElement, fallback: () => void): void {
  const items = focusable(el);
  if (items.length > 0) {
    items[0].focus();
  } else {
    fallback();
  }
}

function styleOverlay(overlay: HTMLElement, layout: ResolvedLayout): void {
  Object.assign(overlay.style, {
    position: "fixed",
    inset: "0",
    display: "flex",
    // A touch-default sheet (task 3.2) sits flush to the bottom edge: its
    // anchor is this flex box's alignment, not a transform applied later, the
    // same way centering is for every other layout.
    alignItems: layout === "sheet" ? "flex-end" : "center",
    justifyContent: "center",
    // Full-screen and the touch sheet have no card-sized gap to leave around
    // the card: both fill or hug an edge of this box, so any padding here
    // would leave a visible band of backdrop nothing can cover.
    padding: layout === "fullscreen" || layout === "sheet" ? "0" : "16px",
    background: "rgba(15, 23, 42, 0.55)",
    zIndex: String(Z_INDEX),
  } as Partial<CSSStyleDeclaration>);
}

/**
 * The card's own padding, as a number because buildCloseRow has to cancel it.
 * One constant rather than two literals: the sticky close row is aligned to the
 * card's corner by negative margins, so a padding change here that did not
 * reach there would slide the X off the corner.
 */
const CARD_PADDING_PX = 24;

/** Gap between a slide-in card and the viewport edges it anchors to. */
const SLIDE_IN_OFFSET_PX = 16;

/** A slide-in card's resting width; `maxWidth` below caps it on a phone. */
const SLIDE_IN_WIDTH_PX = 360;

/** How far a bar travels in from its edge on open, same feel as a slide-in. */
const BAR_ANIM_OFFSET_PX = 16;

/**
 * Which corner a slide-in card anchors to. Only `"bottom-right"`/
 * `"bottom-left"` are meaningful here; `"top"`/`"bottom"` are the bar's own
 * edge values (task 2.4) and, like an absent position, fall back to the
 * engine default named on `FormPopupSettings.position`.
 */
function slideInCorner(
  position: OpenModalOptions["position"],
): "bottom-right" | "bottom-left" {
  return position === "bottom-left" ? "bottom-left" : "bottom-right";
}

/**
 * Which viewport edge a bar sits against. Only `"top"`/`"bottom"` are
 * meaningful here; `"bottom-right"`/`"bottom-left"` are slide-in's own corner
 * values (task 2.3) and, like an absent position, fall back to the engine
 * default named on `FormPopupSettings.position` ("bottom" for bar).
 */
function barEdge(position: OpenModalOptions["position"]): "top" | "bottom" {
  return position === "top" ? "top" : "bottom";
}

function styleCard(
  card: HTMLElement,
  layout: ResolvedLayout,
  position: OpenModalOptions["position"],
): void {
  const shared: Partial<CSSStyleDeclaration> = {
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    // The card itself does not scroll; buildScroller does. That is what keeps
    // the absolutely positioned close button on screen, and it is the whole
    // point of the split (see buildScroller).
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    padding: `${CARD_PADDING_PX}px`,
    background: "#ffffff",
    outline: "none",
  };
  if (layout === "fullscreen") {
    Object.assign(card.style, {
      ...shared,
      height: "100%",
      maxWidth: "none",
      maxHeight: "none",
      borderRadius: "0",
      boxShadow: "none",
    } as Partial<CSSStyleDeclaration>);
  } else if (layout === "slide-in") {
    const corner = slideInCorner(position);
    // No overlay lifts this one above host chrome (see openModal), so the
    // card carries its own z-index and its own shadow does the separation
    // the backdrop would otherwise provide.
    Object.assign(card.style, {
      ...shared,
      position: "fixed",
      width: `${SLIDE_IN_WIDTH_PX}px`,
      maxWidth: `calc(100vw - ${SLIDE_IN_OFFSET_PX * 2}px)`,
      maxHeight: `calc(100vh - ${SLIDE_IN_OFFSET_PX * 2}px)`,
      borderRadius: "12px",
      boxShadow: "0 20px 60px rgba(15, 23, 42, 0.35)",
      zIndex: String(Z_INDEX),
      bottom: `${SLIDE_IN_OFFSET_PX}px`,
      ...(corner === "bottom-left"
        ? { left: `${SLIDE_IN_OFFSET_PX}px` }
        : { right: `${SLIDE_IN_OFFSET_PX}px` }),
    } as Partial<CSSStyleDeclaration>);
  } else if (layout === "bar") {
    const edge = barEdge(position);
    // No overlay lifts this one above host chrome either (see openModal): a
    // full-width strip flush to an edge has no gap around it to dim, and a
    // bar reads as page chrome rather than something that should darken the
    // page behind it. Its own shadow, facing the page, does the separation
    // the backdrop would otherwise provide.
    Object.assign(card.style, {
      ...shared,
      position: "fixed",
      left: "0",
      right: "0",
      maxWidth: "none",
      // Same overflow safety net as every other layout (buildScroller handles
      // the actual scrolling): a bar is meant to be one row tall, but nothing
      // stops an owner's custom CSS or a long validation message from making
      // it not.
      maxHeight: "90vh",
      borderRadius: "0",
      boxShadow:
        edge === "top"
          ? "0 4px 24px rgba(15, 23, 42, 0.2)"
          : "0 -4px 24px rgba(15, 23, 42, 0.2)",
      zIndex: String(Z_INDEX),
      ...(edge === "top" ? { top: "0" } : { bottom: "0" }),
    } as Partial<CSSStyleDeclaration>);
  } else if (layout === "sheet") {
    // Task 3.2: the touch-only default when an owner has not picked a
    // layout. Still a plain modal in `openModal` (backdrop kept, focus
    // trapped, `aria-modal="true"`) — only the shape changes: full width,
    // flush to the bottom edge (styleOverlay's `alignItems: "flex-end"`
    // anchors it there), capped well short of the screen so it never reads
    // as the whole page the way the centered default could on a phone.
    Object.assign(card.style, {
      ...shared,
      width: "100%",
      maxWidth: "none",
      maxHeight: `${TOUCH_SHEET_MAX_HEIGHT_VH}vh`,
      borderRadius: "16px 16px 0 0",
      boxShadow: "0 -4px 24px rgba(15, 23, 42, 0.35)",
    } as Partial<CSSStyleDeclaration>);
  } else {
    Object.assign(card.style, {
      ...shared,
      maxWidth: "480px",
      maxHeight: "90vh",
      borderRadius: "12px",
      boxShadow: "0 20px 60px rgba(15, 23, 42, 0.35)",
    } as Partial<CSSStyleDeclaration>);
  }
}

/**
 * Set a `--kiroku-*` declaration string on the card so the card and the
 * appended form host inherit the form's theme. Only custom properties (`--*`)
 * are applied; malformed fragments are skipped, so a bad theme string can never
 * throw or leak non-theme declarations onto the card.
 *
 * `--kiroku-color-background` and `--kiroku-color-text`, if present, also
 * repaint the card's own `background`/`color` directly (and the close
 * button's painted chip, when passed, since it sits over the card and has to
 * stay legible against whatever the theme sets) rather than through a
 * `var()` reference: this shell ships inside the third-party embed bundle,
 * where the visible color has to be a real value, not an indirection left
 * for the browser to resolve later. Either variable's absence leaves the
 * hardcoded default (set by `styleCard` / `buildCloseButton`) untouched.
 */
function applyThemeVars(
  card: HTMLElement,
  vars: string,
  closeVisual?: HTMLElement,
): void {
  for (const declaration of vars.split(";")) {
    const separator = declaration.indexOf(":");
    if (separator === -1) continue;
    const name = declaration.slice(0, separator).trim();
    const value = declaration.slice(separator + 1).trim();
    if (name.startsWith("--") && value) {
      card.style.setProperty(name, value);
      if (name === "--kiroku-color-background") {
        card.style.background = value;
        if (closeVisual) closeVisual.style.background = value;
      } else if (name === "--kiroku-color-text") {
        card.style.color = value;
        if (closeVisual) closeVisual.style.color = value;
      }
    }
  }
}

/**
 * Inject the form's `settings.customCSS.code` scoped under
 * `[data-kiroku-popup="dialog"]` via CSS nesting, so a bare selector in the
 * owner's stylesheet (e.g. `input { ... }`) styles only what is inside this
 * popup card rather than every matching element on the host page — the same
 * risk `getFormCss` avoids for the inline form by wrapping the same code
 * under `.kiroku-embed-form`. The `<style>` element is a child of the card,
 * so it is removed with the rest of the modal on close; no separate cleanup
 * path is needed. Blank or whitespace-only code renders nothing.
 */
function applyCustomCss(card: HTMLElement, code: string): void {
  const trimmed = code.trim();
  if (!trimmed) return;
  const style = document.createElement("style");
  style.setAttribute("data-kiroku-popup", "custom-css");
  style.textContent = `[data-kiroku-popup="dialog"] {\n${trimmed}\n}`;
  card.appendChild(style);
}

/**
 * WCAG 2.5.5: a tap target should be at least 44x44 CSS px. The painted chip
 * a visitor sees stays 32x32 (`CLOSE_VISIBLE_PX`); only the invisible button
 * around it grows, so the glyph and its rounded background look exactly as
 * they did before.
 */
const CLOSE_HIT_PX = 44;
const CLOSE_VISIBLE_PX = 32;
/** Original absolute offset of the visible chip, preserved below. */
const CLOSE_VISIBLE_OFFSET_PX = 8;
const CLOSE_HIT_OFFSET_PX =
  CLOSE_VISIBLE_OFFSET_PX - (CLOSE_HIT_PX - CLOSE_VISIBLE_PX) / 2;

/**
 * The outer `<button>` is the tap target (`CLOSE_HIT_PX`), transparent and
 * unstyled beyond position and cursor. The inner `data-kiroku-popup=
 * "close-visual"` span is the chip a visitor actually sees, sized and
 * positioned so it lands exactly where the button used to sit; a click
 * anywhere in the button, including the now-invisible margin around the
 * chip, still reaches `onActivate` because the span is inside it.
 */
function buildCloseButton(onActivate: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("aria-label", "Close");
  Object.assign(button.style, {
    position: "absolute",
    top: `${CLOSE_HIT_OFFSET_PX}px`,
    right: `${CLOSE_HIT_OFFSET_PX}px`,
    width: `${CLOSE_HIT_PX}px`,
    height: `${CLOSE_HIT_PX}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0",
    background: "transparent",
    border: "none",
    cursor: "pointer",
  } as Partial<CSSStyleDeclaration>);

  const visual = document.createElement("span");
  visual.setAttribute("data-kiroku-popup", "close-visual");
  visual.setAttribute("aria-hidden", "true");
  visual.textContent = "×"; // ×
  Object.assign(visual.style, {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: `${CLOSE_VISIBLE_PX}px`,
    height: `${CLOSE_VISIBLE_PX}px`,
    lineHeight: "1",
    fontSize: "22px",
    color: "#334155",
    // Opaque rather than transparent, because the row below keeps this chip
    // over the content while that content scrolls under it. Matches the
    // card's own background; applyThemeVars repaints both together when the
    // form has a theme (openModal passes this element to it for exactly that
    // reason).
    background: "#ffffff",
    borderRadius: "8px",
  } as Partial<CSSStyleDeclaration>);
  button.appendChild(visual);

  button.addEventListener("click", onActivate);
  return button;
}

/**
 * The box that scrolls, so that the card does not.
 *
 * The card used to be `overflow: auto` itself, which meant the close button,
 * absolutely positioned against the card, was laid out in the scrolled content
 * and travelled up out of the viewport with it. Scrolled far enough there was
 * no visible way out: on a phone the overlay is a 16px band at 390px wide, and
 * there is no Escape key.
 *
 * Found on 2026-09-03 by the `quiz` flow in scripts/record-flow.ts, which is
 * what CLAUDE.md 7 says recordings are for. The quiz result screen measured
 * 812px inside a 760px card at 390x844. The defect is older than the quiz and
 * belongs to any popup with content taller than 90vh; a computed result is
 * simply the first thing we ship that reliably is.
 *
 * `minHeight: 0` because a flex item's default `min-height: auto` refuses to
 * shrink below its content, which would push the card past 90vh and put the
 * scrollbar back on the page instead of in here.
 */
function buildScroller(): HTMLElement {
  const scroller = document.createElement("div");
  scroller.setAttribute("data-kiroku-popup", "scroll");
  Object.assign(scroller.style, {
    overflow: "auto",
    minHeight: "0",
  } as Partial<CSSStyleDeclaration>);
  return scroller;
}

/** Play the open transition unless motion is disabled. */
function animateIn(overlay: HTMLElement, card: HTMLElement): void {
  overlay.style.opacity = "0";
  card.style.opacity = "0";
  card.style.transform = "translateY(8px) scale(0.98)";
  // Force a reflow so the transition has an initial frame to animate from.
  void card.offsetHeight;
  overlay.style.transition = `opacity ${ANIM_MS}ms ease-out`;
  card.style.transition = `opacity ${ANIM_MS}ms ease-out, transform ${ANIM_MS}ms ease-out`;
  overlay.style.opacity = "1";
  card.style.opacity = "1";
  card.style.transform = "translateY(0) scale(1)";
}

/**
 * Play the slide-in card's own open transition unless motion is disabled.
 * There is no overlay to fade (slide-in has no backdrop, see the module
 * comment and `openModal`): only the card moves, lifting in from just past
 * its resting corner offset.
 */
function animateInSlide(card: HTMLElement): void {
  card.style.opacity = "0";
  card.style.transform = `translateY(${SLIDE_IN_OFFSET_PX}px)`;
  // Force a reflow so the transition has an initial frame to animate from.
  void card.offsetHeight;
  card.style.transition = `opacity ${ANIM_MS}ms ease-out, transform ${ANIM_MS}ms ease-out`;
  card.style.opacity = "1";
  card.style.transform = "translateY(0)";
}

/**
 * Play a bar's own open transition unless motion is disabled. Same shape as
 * `animateInSlide` (no overlay to fade, only the card moves), but the travel
 * direction matches the edge it rests against: a bottom bar lifts up, a top
 * bar drops down, so the motion reads as arriving from off-screen rather than
 * from inside the page.
 */
function animateInBar(card: HTMLElement, edge: "top" | "bottom"): void {
  card.style.opacity = "0";
  card.style.transform = `translateY(${edge === "top" ? -BAR_ANIM_OFFSET_PX : BAR_ANIM_OFFSET_PX}px)`;
  // Force a reflow so the transition has an initial frame to animate from.
  void card.offsetHeight;
  card.style.transition = `opacity ${ANIM_MS}ms ease-out, transform ${ANIM_MS}ms ease-out`;
  card.style.opacity = "1";
  card.style.transform = "translateY(0)";
}

/**
 * Lay a bar's content out as one row that wraps, rather than the vertical
 * stack every other layout leaves to the caller's own markup. `contentEl`'s
 * direct children are what "the fields" means here — this shell has no other
 * way to reach into whatever renderer produced them — and `normalizePopupSettings`
 * (task 2.4) only lets a `"bar"` popup be stored for a form with at most
 * `POPUP_BAR_MAX_VISIBLE_FIELDS` visible fields, so a bar's content is always
 * short enough for one row to be the right default.
 */
function styleBarRow(contentEl: HTMLElement): void {
  Object.assign(contentEl.style, {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.75rem",
  } as Partial<CSSStyleDeclaration>);
}

/**
 * Presentation for the offer screen's Yes button: the ordinary primary
 * button, sized off the same theme variables as the rest of the card. Not
 * exported like `DECLINE_BUTTON_STYLE`: this screen renders in one place
 * (this module — the dashboard preview and the embed both open it through
 * `openModal`), so there is nothing else for a guard test to hold in sync.
 */
const OFFER_YES_BUTTON_STYLE = {
  display: "inline-block",
  padding:
    "var(--kiroku-spacing-small, 0.5rem) var(--kiroku-spacing-large, 1.5rem)",
  fontSize: "var(--kiroku-font-size, 1rem)",
  fontWeight: 600,
  lineHeight: "1.5",
  color: "#ffffff",
  background: "var(--kiroku-color-primary, #4f46e5)",
  border: "1px solid transparent",
  borderRadius: "var(--kiroku-border-radius, 0.375rem)",
  cursor: "pointer",
} as const;

/**
 * The offer screen: a question and two buttons. Text goes in as text nodes,
 * never `innerHTML` — this markup renders a question and two labels the form
 * owner typed, on the customer's page. The No button reuses
 * `DECLINE_BUTTON_STYLE` and `DECLINE_ROW_STYLE` so it carries the same peer
 * weight as the standalone decline control
 * (`tests/guard/offer-screen-no-is-neutral.test.ts`).
 *
 * `reveal` swaps the screen out for `contentEl`; it is called before
 * `offer.onYes`, per the doc comment on `OpenModalOptions.offer.onYes`. A
 * blank `yesLabel`/`noLabel` resolves to `DEFAULT_OFFER_YES_TEXT`/
 * `DEFAULT_DECLINE_TEXT`.
 */
function buildOfferScreen(
  offer: NonNullable<OpenModalOptions["offer"]>,
  reveal: () => void,
): { el: HTMLElement; yesButton: HTMLButtonElement } {
  const screen = document.createElement("div");
  screen.setAttribute("data-kiroku-popup", "offer-screen");

  const heading = document.createElement("h2");
  heading.textContent = offer.question;
  Object.assign(heading.style, {
    margin: "0 0 1rem",
    fontSize: "1.125rem",
    fontWeight: "600",
    color: "#0f172a",
    textAlign: "center",
  } as Partial<CSSStyleDeclaration>);

  const yesButton = document.createElement("button");
  yesButton.type = "button";
  yesButton.textContent = offer.yesLabel || DEFAULT_OFFER_YES_TEXT;
  Object.assign(yesButton.style, OFFER_YES_BUTTON_STYLE);
  const yesRow = document.createElement("div");
  Object.assign(yesRow.style, {
    display: "flex",
    justifyContent: "center",
  } as Partial<CSSStyleDeclaration>);
  yesRow.appendChild(yesButton);
  yesButton.addEventListener("click", () => {
    reveal();
    offer.onYes();
  });

  const noButton = document.createElement("button");
  noButton.type = "button";
  noButton.textContent = offer.noLabel || DEFAULT_DECLINE_TEXT;
  Object.assign(noButton.style, DECLINE_BUTTON_STYLE);
  const noRow = document.createElement("div");
  Object.assign(noRow.style, DECLINE_ROW_STYLE);
  noRow.appendChild(noButton);
  noButton.addEventListener("click", () => {
    // Closes like a user dismissal, but onNo (not onClose) is the caller's
    // signal — the offer screen's "No" is the decline control, not a close.
    closeModal();
    offer.onNo();
  });

  screen.appendChild(heading);
  screen.appendChild(yesRow);
  screen.appendChild(noRow);
  return { el: screen, yesButton };
}

/**
 * Open `contentEl` in an accessible, focus-trapped modal. Any previously open
 * modal is torn down first. Returns nothing; close via `closeModal()` or a user
 * dismissal (which also invokes `options.onClose`).
 */
export function openModal(
  contentEl: HTMLElement,
  options: OpenModalOptions = {},
): void {
  // Tear down a stale modal without invoking its onClose (programmatic replace).
  if (active) closeModal();

  const previouslyFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

  // Task 3.2: an owner who has not picked a layout gets the centered default
  // on a computer and the bottom sheet on a touch device; any explicit
  // choice, `"modal"` included, always wins and this never runs.
  const effectiveLayout: ResolvedLayout =
    options.layout ?? (currentDeviceIsTouch() ? "sheet" : undefined);

  // Slide-in and bar have no backdrop (module comment, tasks 2.3/2.4):
  // nothing dims the page, so there is no overlay element to create, and the
  // card is what gets appended straight to `document.body` further down.
  // The touch sheet is not in this list: it keeps the backdrop and the focus
  // trap, unlike slide-in/bar, only its shape differs from the centered card.
  const isSlideIn = effectiveLayout === "slide-in";
  const isBar = effectiveLayout === "bar";
  const isNonModal = isSlideIn || isBar;

  const overlay: HTMLElement | null = isNonModal
    ? null
    : document.createElement("div");
  if (overlay) {
    overlay.setAttribute("data-kiroku-popup", "overlay");
    styleOverlay(overlay, effectiveLayout);
  }

  const card = document.createElement("div");
  card.setAttribute("data-kiroku-popup", "dialog");
  card.setAttribute("role", "dialog");
  // A slide-in or bar card is not modal: neither has a backdrop or traps
  // focus, so the page behind it stays reachable and usable.
  card.setAttribute("aria-modal", isNonModal ? "false" : "true");
  card.tabIndex = -1;
  styleCard(card, effectiveLayout, options.position);

  const handleUserClose = (): void => {
    const cb = active?.onClose;
    closeModal();
    if (cb) cb();
  };

  // The button is a child of the card, not of the scroller, which is what
  // keeps it on screen while the content moves under it. Built before
  // applyThemeVars/applyCustomCss so both can reach it: the former repaints
  // it alongside the card, the latter's scoped selector covers the whole
  // dialog, this button included.
  const closeButton = buildCloseButton(handleUserClose);
  card.appendChild(closeButton);
  const scroller = buildScroller();
  card.appendChild(scroller);

  // applyThemeVars repaints the painted chip, not the (transparent) tap
  // target around it: that span is what a visitor sees.
  const closeVisual = closeButton.querySelector<HTMLElement>(
    '[data-kiroku-popup="close-visual"]',
  );
  if (options.vars)
    applyThemeVars(card, options.vars, closeVisual ?? undefined);
  if (options.customCSS) applyCustomCss(card, options.customCSS);

  // The offer screen swaps in for contentEl rather than sitting beside it:
  // contentEl is not appended (and its reassurance line not applied) until
  // "Yes", so it does not exist for the focus trap to find while hidden.
  let nameSource: HTMLElement = contentEl;
  let initialFocus: HTMLElement | null = null;
  if (options.offer) {
    const { el: screen, yesButton } = buildOfferScreen(options.offer, () => {
      screen.remove();
      scroller.appendChild(contentEl);
      if (isBar) styleBarRow(contentEl);
      applyReassurance(scroller, options.privacyPolicyUrl);
      focusFirstWithin(contentEl, () => focusInto(card));
    });
    scroller.appendChild(screen);
    // The offer screen's own children (the question, the Yes row, the No row)
    // get the same wrapping-row treatment as the form it reveals (task 2.5):
    // a bar is one row tall, and a question stacked above two button rows
    // would not fit it.
    if (isBar) styleBarRow(screen);
    nameSource = screen;
    initialFocus = yesButton;
  } else {
    scroller.appendChild(contentEl);
    // After the append, so a caller who passes something that is not a node
    // gets appendChild's own error naming the argument, rather than one from
    // reading a property off it in here.
    if (isBar) styleBarRow(contentEl);
    applyReassurance(scroller, options.privacyPolicyUrl);
  }
  nameDialog(card, nameSource, options.label);
  if (overlay) overlay.appendChild(card);

  if (overlay) {
    // Backdrop click (outside the card) dismisses. Slide-in/bar have no
    // backdrop to click, and nothing else on the page should close it on
    // their behalf.
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) handleUserClose();
    });
  }

  const keydownHandler = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleUserClose();
    } else if (event.key === "Tab" && !isNonModal) {
      // Slide-in/bar trap nothing: Tab is left to the browser's own default
      // handling, so the rest of the page stays reachable while it is open.
      trapTab(event, card);
    }
  };
  document.addEventListener("keydown", keydownHandler, true);

  const root = overlay ?? card;
  document.body.appendChild(root);

  // Slide-in/bar are not modal (module comment, `isNonModal` above): the page
  // behind them stays reachable on purpose, so their siblings are never
  // marked inert. Everything else here has a backdrop and traps focus, so the
  // rest of `document.body` is unreachable either way; inert makes that true
  // for assistive tech too.
  const inertSiblings = isNonModal ? [] : applyInertToSiblings(root);

  const reducedMotion = options.reducedMotion ?? prefersReducedMotion();
  if (!reducedMotion) {
    if (isSlideIn) {
      animateInSlide(card);
    } else if (isBar) {
      animateInBar(card, barEdge(options.position));
    } else if (overlay) {
      animateIn(overlay, card);
    }
  }

  active = {
    root,
    card,
    previouslyFocused,
    onClose: options.onClose,
    keydownHandler,
    inertSiblings,
  };

  // The offer screen's Yes gets focus directly: focusInto(card) would land on
  // the close X, which is earlier in the card's DOM order than anything the
  // screen builds (see buildCloseButton above).
  if (initialFocus) {
    initialFocus.focus();
  } else {
    focusInto(card);
  }
}

/**
 * Close the active modal and restore focus. No-op when nothing is open. Does
 * not invoke `onClose` — that fires only on user dismissal.
 */
export function closeModal(): void {
  if (!active) return;
  const { root, previouslyFocused, keydownHandler, inertSiblings } = active;
  active = null;

  document.removeEventListener("keydown", keydownHandler, true);
  if (root.parentNode) root.parentNode.removeChild(root);
  // Before restoring focus: a previously focused sibling could be one of the
  // elements this modal marked inert, and inert must be gone from it before
  // anything tries to focus it again.
  restoreInert(inertSiblings);

  if (
    previouslyFocused &&
    document.contains(previouslyFocused) &&
    typeof previouslyFocused.focus === "function"
  ) {
    previouslyFocused.focus();
  }
}

/** Whether a modal is currently open (used by the embed to avoid re-opening). */
export function isModalOpen(): boolean {
  return active !== null;
}
